<template>
    <div>
        <hts-data-table
            ref="dataTables"
            :headers="headers"
            :server="server" />

        <update-status
            v-if="checkPermission(layoutPermissions.journey_management)"
            ref="updatStatus"
            @refreshTables="refreshTables" />

        <change-partner
            v-if="checkPermission(layoutPermissions.journey_management)"
            ref="changePartner"
            @refreshTables="refreshTables" />

        <container-no
            v-if="checkPermission(layoutPermissions.journey_management)"
            ref="containerNo"
            @refreshTables="refreshTables" />

        <journey-info
            ref="journeyInfoComponent"
            :api="$url.monitoring.journey.getDetail"
            :exclude="journeyInfoExclude" />
    </div>
</template>

<script setup lang="ts">
import JourneyInfo from '../-components/journey-info.vue'
import Deadline from './-components/deadline.vue'
import ChangePartner from './-components/journey/change-partner.vue'
import ContainerNo from './-components/journey/container-no.vue'
import UpdateStatus from './-components/journey/update-status.vue'
import Request from './-components/request.vue'
import Route from './-components/route.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

import Status from '~/components/status.vue'

const { $formatters, $location } = useNuxtApp()

const server = $url.trip.journey['get-list']
const journeyInfoExclude = computed<any[]>(() => ['time'])
const journeyInfoComponent: any = ref()

const route = useRoute()
const action = computed(() => String(route.query.action) || '')
const journeyId = computed(() => String(route.query.journeyId) || '')

onMounted(() => {
  handleOnMountPage()
})

watch(
  () => action.value,
  () => {
    handleOnMountPage()
  }
)
watch(
  () => journeyId.value,
  () => {
    handleOnMountPage()
  }
)

const handleOnMountPage = () => {
  if (action.value === 'view' && journeyId.value) {
    journeyInfoComponent.value && journeyInfoComponent.value.show(journeyId.value)
  }
}

const dataTables = ref()
const updatStatus = ref()
const changePartner = ref()
const containerNo = ref()

const handleCancel = (data: any) => {
  if (updatStatus.value) {
    updatStatus.value.open(data._id)
  }
}

const handleCreateContainerNumbers = (data: any) => {
  if (containerNo.value) {
    containerNo.value.open(data._id)
  }
}

const handleEditContainerNumbers = (data: any) => {
  if (containerNo.value) {
    containerNo.value.open(data._id, data.containerNumbers)
  }
}

const checkNotCancel = (data: any) => {
  const status = data.status

  if (typeof status !== 'number') {
    return false
  }

  if ([-1].includes(status)) {
    return false
  }

  return true
}

const checkCreateContainerNumber = (data: any) => {
  if (!checkNotCancel(data)) {
    return false
  }

  if (!Array.isArray(data.containerNumbers) || !data.containerNumbers.length) {
    return true
  }

  return false
}

const checkEditContainerNumber = (data: any) => {
  if (!checkNotCancel(data)) {
    return false
  }

  if (Array.isArray(data.containerNumbers) && data.containerNumbers.length) {
    return true
  }

  return false
}

const openView = (data: any) => {
  journeyInfoComponent.value && journeyInfoComponent.value.show(data._id)
}

const handleChangePartner = (data: any) => {
  if (changePartner.value) {
    changePartner.value.open(data._id)
  }
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'containerNumbers',
    headerName: $t('labels.containerNo'),
    valueGetter: (params: any) => {
      const { containerNumbers } = params.data || {}

      if (!containerNumbers) {
        return
      }

      if (!Array.isArray(containerNumbers)) {
        return ''
      }

      const a = containerNumbers.map((item: any) => item.containerNumber)
      return a.join(', ')
    }
    // sortable: true
    // filter: 'agTextColumnFilter'
  },
  {
    field: 'contCategory',
    headerName: $t('labels.containerType'),
    valueGetter: (params: any) => {
      const { contCategory, goodsStatusName } = params.data?.orderData || {}
      let text = ''
      if (!contCategory) {
        return
      }

      if (contCategory === 20) {
        text = $t('labels.20f')
      } else if (contCategory === 40) {
        text = $t('labels.40f')
      } else if (contCategory === 45) {
        text = $t('labels.45f')
      }

      text += ` - ${goodsStatusName || ''}`
      return text
    }
  },
  {
    field: 'vehicleLicense',
    headerName: 'Phương tiện kéo',
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      const vehicleLicensePlate = params.data?.vehicleLicensePlate
      const vehicleTempLicensePlate = params.data?.vehicleTempLicensePlate
      return vehicleLicensePlate || vehicleTempLicensePlate
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'journeyStatus',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      {
        name: $t('labels.cancel'),
        value: -1,
        class: { '!bg-error-100': true, '!text-error-800': true } // Màu đỏ (#F44336)
      },
      {
        name: 'Chờ xác nhận',
        value: 1,
        class: { '!bg-yellow-100': true, '!text-yellow-800': true } // Màu vàng (#FFEB3B)
      },
      {
        name: 'Chờ điều đội xe',
        value: 7,
        class: { '!bg-sky-100': true, '!text-sky-800': true } // Màu xanh nhạt (#81D4FA)
      },
      {
        name: 'Chờ điều xe',
        value: 2,
        class: { '!bg-blue-100': true, '!text-blue-800': true } // Màu xanh dương (#2196F3)
      },
      {
        name: 'Có xe dự kiến',
        value: 3,
        class: { '!bg-orange-100': true, '!text-orange-800': true } // Màu cam (#FF9800)
      },
      {
        name: 'Có xe chính thức',
        value: 4,
        class: { '!bg-green-100': true, '!text-green-800': true } // Màu xanh lá cây (#4CAF50)
      },
      {
        name: 'Đang vận chuyển',
        value: 5,
        class: { '!bg-green-200': true, '!text-green-800': true } // Màu xanh lá cây đậm (#388E3C)
      },
      {
        name: 'Kết thúc chặng',
        value: 6,
        class: { '!bg-gray-100': true, '!text-gray-800': true } // Màu xám (#9E9E9E)
      }
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'workflowName',
    headerName: $t('labels.workflow'),
    valueGetter: (params: any) => {
      return params.data.orderData?.workflowName
    }
  },
  {
    field: 'journey',
    headerName: $t('labels.route'),
    minWidth: 700,
    filter: 'agTextColumnFilter',
    cellRenderer: Route
  },
  {
    field: 'deadline',
    headerName: $t('labels.deadline'),
    autoHeight: true,
    cellRenderer: Deadline
  },
  {
    field: 'request',
    headerName: $t('labels.note'),
    cellRenderer: Request
  },
  {
    field: 'orderCode',
    headerName: $t('labels.order'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'startAt',
    headerName: 'Thời gian dự kiến khởi hành',
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      const data = params.data.startAt
      if (!data) {
        return ''
      }

      return $formatters.datetime(data)
    }
  },
  {
    headerName: 'Thời gian thực hiện chặng hàng',
    valueGetter: (params: any) => {
      const timeIn = params.data?.timeIn
      const timeOut = params.data?.timeOut
      return $formatters.datetime(timeIn) + ' - ' + $formatters.datetime(timeOut)
    }
  },
  {
    field: 'estimateStartTime.departureLocationName',
    headerName: 'Địa điểm khởi hành',
    valueGetter: (params: any) => {
      const startAtLocation = params.data?.startAtLocation
      const estimateStartTime = params.data?.estimateStartTime

      if (startAtLocation) {
        return startAtLocation.locationName
      }
      // if (startAtLocationId) {
      //   return $location.getLocation(startAtLocationId)?.locationName
      // }

      return estimateStartTime?.departureLocationName || ''
    }
  },
  {
    field: 'partnerName',
    headerName: $t('labels.fleetVehicle'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: 'Yêu cầu đặc biệt',
    valueGetter: (params: any) => {
      const results = [] as string[]
      if (params?.data?.orderData?.mergeContainer) {
        results.push($t('labels.mergeContainer'))
      }

      if (params?.data?.orderData?.headContainer) {
        results.push($t('labels.headContainer'))
      }

      return results.join(', ')
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'tripDataTable',
      storeName: 'Trip',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          click: openView
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.changePartner'),
          class: 'text-blue-500 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          isAll: true,
          validate: checkNotCancel,
          click: handleChangePartner
        },
        {
          icon: 'hi-pencil-square-icon',
          label: 'Thêm số container',
          class: 'text-blue-500 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          isAll: true,
          validate: checkCreateContainerNumber,
          click: handleCreateContainerNumbers
        },
        {
          icon: 'hi-pencil-square-icon',
          label: 'Chỉnh sửa số container',
          class: 'text-blue-500 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          isAll: true,
          validate: checkEditContainerNumber,
          click: handleEditContainerNumbers
        },
        {
          icon: 'hi-x-circle-icon',
          label: $t('labels.cancel'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          validate: checkNotCancel,
          isAll: true,
          click: handleCancel
        }
      ]
    }
  }
])
</script>
